/* LAB-106 · Change color or size from the cart (drawer and /cart page).
 *
 * Research: analysis/ux/2026-09-29-cart-variant-edit-research.md. Each editable cart line carries an "Edit" button
 * (snippets/cart-products.liquid, [data-lb-edit]). It opens a modal sheet (bottom sheet on phones, centred on desktop)
 * with the product's options as chips: color first, then size; sold-out values struck through; the size finder's
 * answer for that product named under the size row (localStorage lb_reco_v1.recs, written by assets/lb-size-reco.js).
 *
 * Shopify's /cart/change.js cannot switch a line to another variant, so a change is two calls:
 *   add.js (new variant, same quantity, same properties) -> change.js (old line minus that quantity, with sections)
 * If the second call fails the first is reversed. The section HTML goes out on a CartLinesUpdateEvent, which is how
 * Horizon's own cart-items-component morphs the drawer and the cart page. After the morph the row gets an inline
 * "Changed to … · Undo" note (inline, not a toast: the drawer is a modal dialog and anything outside it is inert).
 *
 * Analytics: PostHog cart_edit_opened / cart_edit_saved / cart_edit_failed / cart_edit_undone. The swap also fires
 * Shopify's own add/remove cart events (unavoidable with add.js); cart_edit_saved is what separates them.
 * window.__lbCartEdit keeps a log for the harness (scripts/qa-cart-edit.py).
 */
import { CartLinesUpdateEvent } from '@shopify/events';

const ROOT = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
const REC_KEY = 'lb_reco_v1';
const PAIR_RE = /^Kids (.+) \+ Adult (.+)$/;
const log = (window.__lbCartEdit = window.__lbCartEdit || []);
const productCache = new Map();

function track(name, props) {
  log.push({ name, props, t: Date.now() });
  try {
    if (window.posthog && typeof window.posthog.capture === 'function') window.posthog.capture(name, props || {});
  } catch (e) { /* analytics never blocks the cart */ }
}

function el(tag, cls, text) {
  const n = document.createElement(tag);
  if (cls) n.className = cls;
  if (text != null) n.textContent = text;
  return n;
}

function money(cents) {
  const cur = (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'USD';
  try {
    return new Intl.NumberFormat(document.documentElement.lang || 'en', { style: 'currency', currency: cur }).format(cents / 100);
  } catch (e) {
    return (cents / 100).toFixed(2);
  }
}

function loadProduct(handle) {
  if (!productCache.has(handle)) {
    const url = `${ROOT}products/${encodeURIComponent(handle)}.js`;
    const get = () => fetch(url, { headers: { Accept: 'application/json' } });
    // Shopify's bot check can answer 429 in the first seconds after a page load (seen 2026-09-29); one retry covers it
    const p = get()
      .then((r) => (r.status === 429 ? new Promise((ok) => setTimeout(ok, 1500)).then(get) : r))
      .then((r) => {
        if (!r.ok) throw new Error(`product ${r.status}`);
        return r.json();
      });
    p.catch(() => productCache.delete(handle));
    productCache.set(handle, p);
  }
  return productCache.get(handle);
}

function readRec(handle) {
  try {
    const s = JSON.parse(localStorage.getItem(REC_KEY) || '{}') || {};
    return (s.recs && s.recs[handle]) || null;
  } catch (e) {
    return null;
  }
}

async function postJSON(path, body) {
  const r = await fetch(`${ROOT}${path}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
    body: JSON.stringify(body),
  });
  let data = {};
  try { data = await r.json(); } catch (e) { /* empty body */ }
  if (!r.ok || data.status >= 400 || data.errors) {
    const msg = data.description || (typeof data.errors === 'string' ? data.errors : '') || data.message || '';
    const err = new Error(msg || `HTTP ${r.status}`);
    err.userMessage = msg;
    throw err;
  }
  return data;
}

async function getCart() {
  const r = await fetch(`${ROOT}cart.js`, { headers: { Accept: 'application/json' } });
  return r.json();
}

function sectionIds() {
  const ids = new Set();
  document.querySelectorAll('cart-items-component').forEach((c) => {
    if (c instanceof HTMLElement && c.dataset.sectionId) ids.add(c.dataset.sectionId);
  });
  return Array.from(ids);
}

/* Move `qty` units of the line `fromKey` (variant `fromVariant`) to variant `toVariant`. Returns the change.js response
 * (with sections) and the key of the line that now holds the new variant. */
async function swap({ fromKey, fromVariant, toVariant, qty, properties }) {
  const item = { id: toVariant, quantity: qty };
  if (properties && Object.keys(properties).length) item.properties = properties;
  const added = await postJSON('cart/add.js', { items: [item] });
  const newKey = added.items && added.items[0] && added.items[0].key;

  const cart = await getCart();
  const old = cart.items.find((i) => i.key === fromKey) || cart.items.find((i) => i.variant_id === fromVariant && i.key !== newKey);
  const sections = sectionIds();
  try {
    const res = await postJSON('cart/change.js', {
      id: old ? old.key : fromKey,
      quantity: old ? Math.max(0, old.quantity - qty) : 0,
      sections: sections.join(','),
      sections_url: window.location.pathname,
    });
    const line = res.items.find((i) => i.key === newKey) || res.items.find((i) => i.variant_id === toVariant);
    return { res, newKey: line ? line.key : newKey, sections };
  } catch (err) {
    // put the cart back the way it was: take the added units off the new line
    try {
      const now = await getCart();
      const nl = now.items.find((i) => i.key === newKey) || now.items.find((i) => i.variant_id === toVariant);
      if (nl) await postJSON('cart/change.js', { id: nl.key, quantity: Math.max(0, nl.quantity - qty) });
    } catch (e) { /* the error below still reaches the shopper */ }
    throw err;
  }
}

function announceCart(res, sections, newKey, qty, source) {
  const deferred = CartLinesUpdateEvent.createPromise();
  document.body.dispatchEvent(
    new CartLinesUpdateEvent({
      action: 'update',
      context: 'cart',
      lines: [{ id: newKey, quantity: qty }],
      promise: deferred.promise,
    })
  );
  deferred.resolve({
    cart: CartLinesUpdateEvent.createCartFromAjaxResponse(res),
    detail: {
      sections: res.sections,
      items: res.items,
      itemCount: res.item_count,
      source,
      didError: false,
    },
  });
}

/* Wait until every cart-items-component shows a row for `key` (the morph has landed). */
function waitForRow(key, goneKey, timeout = 2500) {
  const start = performance.now();
  return new Promise((resolve) => {
    const tick = () => {
      const rows = Array.from(document.querySelectorAll(`tr[data-key="${CSS.escape(key)}"]`));
      const gone = !goneKey || goneKey === key || !document.querySelector(`tr[data-key="${CSS.escape(goneKey)}"]`);
      if ((rows.length && gone) || performance.now() - start > timeout) return resolve(rows);
      requestAnimationFrame(tick);
    };
    requestAnimationFrame(tick);
  });
}

/* ---------------- the sheet ---------------- */

let dialog = null;
let state = null;

function ensureDialog() {
  if (dialog) return dialog;
  injectStyles();
  dialog = el('dialog', 'lb-ce');
  dialog.setAttribute('aria-labelledby', 'lb-ce-title');
  dialog.innerHTML =
    '<form method="dialog" class="lb-ce__inner">' +
    '<div class="lb-ce__head"><div><h2 class="lb-ce__title" id="lb-ce-title">Change color or size</h2>' +
    '<p class="lb-ce__product"></p></div>' +
    '<button type="button" class="lb-ce__close" aria-label="Close" data-lb-ce-close>&times;</button></div>' +
    '<div class="lb-ce__summary"><img class="lb-ce__img" alt="" width="72" height="72" hidden><p class="lb-ce__choice" aria-live="polite"></p></div>' +
    '<div class="lb-ce__body"></div>' +
    '<p class="lb-ce__msg" role="alert" hidden></p>' +
    '<div class="lb-ce__foot"><button type="submit" class="lb-ce__save button" data-lb-ce-save>Update cart</button>' +
    '<a class="lb-ce__link" href="#">Size chart on the product page</a></div>' +
    '</form>';
  document.body.appendChild(dialog);

  dialog.addEventListener('click', (ev) => {
    ev.stopPropagation();
    if (ev.target === dialog) close('backdrop'); // a click on the backdrop lands on the dialog itself
    if (ev.target.closest('[data-lb-ce-close]')) close('button');
  });
  dialog.addEventListener('keydown', (ev) => {
    if (ev.key === 'Escape') {
      ev.preventDefault(); // Horizon's drawer closes on any Escape that is not defaultPrevented
      ev.stopPropagation();
      close('escape');
    }
  });
  dialog.addEventListener('cancel', (ev) => {
    ev.preventDefault();
    close('escape');
  });
  dialog.querySelector('form').addEventListener('submit', (ev) => {
    ev.preventDefault();
    save();
  });
  dialog.addEventListener('change', (ev) => {
    const input = ev.target;
    if (!(input instanceof HTMLInputElement) || !state) return;
    state.sel[input.name] = input.value;
    render();
  });
  return dialog;
}

function close(how) {
  if (!dialog || !dialog.open) return;
  dialog.close();
  if (state && state.opener && state.opener.isConnected) state.opener.focus();
  if (state && how) track('cart_edit_closed', { product: state.line.handle, how, context: state.line.context });
  state = null;
}

/* The controls: one per real option, except the Pair's consolidated "Kids X + Adult Y" option, which is shown as two
 * rows (as on the product page, assets/pair-color-rows.js). Color rows come first, sizes after. */
function buildControls(product) {
  const opts = product.options.map((o, i) => (typeof o === 'string' ? { name: o, values: uniq(product.variants.map((v) => v.options[i])), position: i } : { name: o.name, values: o.values, position: i }));
  const controls = [];
  for (const o of opts) {
    const pair = o.values.length > 1 && o.values.every((v) => PAIR_RE.test(v));
    if (pair) {
      const kids = uniq(o.values.map((v) => v.match(PAIR_RE)[1]));
      const adults = uniq(o.values.map((v) => v.match(PAIR_RE)[2]));
      controls.push({ id: `o${o.position}k`, label: 'Kids color', option: o.position, part: 1, values: kids, color: true });
      controls.push({ id: `o${o.position}a`, label: 'Adult color', option: o.position, part: 2, values: adults, color: true });
    } else {
      const color = /colou?r/i.test(o.name);
      controls.push({ id: `o${o.position}`, label: o.name, option: o.position, part: 0, values: o.values, color });
    }
  }
  return controls.sort((a, b) => (b.color ? 1 : 0) - (a.color ? 1 : 0));
}

function uniq(a) {
  return Array.from(new Set(a));
}

/* control selections -> the option values of a variant */
function optionValues(controls, sel, nOptions) {
  const vals = new Array(nOptions).fill(null);
  const parts = {};
  for (const c of controls) {
    if (c.part === 0) vals[c.option] = sel[c.id];
    else (parts[c.option] = parts[c.option] || {})[c.part] = sel[c.id];
  }
  for (const [pos, p] of Object.entries(parts)) vals[+pos] = `Kids ${p[1]} + Adult ${p[2]}`;
  return vals;
}

function findVariant(product, vals) {
  return product.variants.find((v) => v.options.every((o, i) => o === vals[i])) || null;
}

/* chip state: colors ignore the size rows (a color is offered if any size of it is in stock); sizes follow the colors */
function chipState(c, value) {
  const { product, controls, sel } = state;
  const trial = { ...sel, [c.id]: value };
  const relevant = controls.filter((o) => o === c || (c.color ? o.color : true));
  const matches = product.variants.filter((v) =>
    relevant.every((o) => {
      const val = o.part === 0 ? v.options[o.option] : (v.options[o.option].match(PAIR_RE) || [])[o.part];
      return val === trial[o.id];
    })
  );
  if (!matches.length) return 'none';
  return matches.some((v) => v.available) ? 'ok' : 'soldout';
}

function selectionFromVariant(controls, variant) {
  const sel = {};
  for (const c of controls) {
    const raw = variant.options[c.option];
    sel[c.id] = c.part === 0 ? raw : (raw.match(PAIR_RE) || [])[c.part];
  }
  return sel;
}

function describe(controls, sel) {
  // the Pair reads per person ("Kids Pink, 120 · Adult White, XS"); everything else "White / M"
  const kids = controls.filter((c) => /^kids/i.test(c.label));
  const adult = controls.filter((c) => /^adult/i.test(c.label));
  if (kids.length && adult.length) {
    const part = (cs, who) => `${who} ${cs.map((c) => sel[c.id]).join(', ')}`;
    return `${part(kids, 'Kids')} · ${part(adult, 'Adult')}`;
  }
  return controls.map((c) => sel[c.id]).join(' / ');
}

function render() {
  const { product, controls, sel, line, rec } = state;
  const body = dialog.querySelector('.lb-ce__body');
  body.innerHTML = '';
  for (const c of controls) {
    const fs = el('fieldset', 'lb-ce__group');
    fs.appendChild(el('legend', 'lb-ce__legend', c.label));
    const row = el('div', 'lb-ce__chips');
    for (const v of c.values) {
      const st = chipState(c, v);
      const lab = el('label', `lb-ce__chip lb-ce__chip--${st}`);
      const input = document.createElement('input');
      input.type = 'radio';
      input.name = c.id;
      input.value = v;
      input.checked = sel[c.id] === v;
      if (st === 'none') input.disabled = true;
      lab.appendChild(input);
      const fill = c.color && c.part === 0 && state.colors && state.colors[v];
      if (fill) {
        lab.classList.add('lb-ce__chip--swatch');
        const d = el('span', 'lb-ce__dot');
        d.style.background = fill;
        d.setAttribute('aria-hidden', 'true');
        lab.appendChild(d);
      }
      lab.appendChild(el('span', 'lb-ce__name', v));
      if (state.cartSel && state.cartSel[c.id] === v) {
        lab.classList.add('lb-ce__chip--incart');
        lab.appendChild(el('span', 'lb-ce__incart', 'in cart'));
      }
      if (st === 'soldout') lab.appendChild(el('span', 'visually-hidden', ' (sold out)'));
      if (rec && recFor(c, rec) === v) lab.classList.add('lb-ce__chip--rec');
      row.appendChild(lab);
    }
    fs.appendChild(row);
    const r = rec && recFor(c, rec);
    if (r && c.values.includes(r)) fs.appendChild(el('p', 'lb-ce__rec', `The size finder suggested ${r} for this one.`));
    body.appendChild(fs);
  }

  const vals = optionValues(controls, sel, product.options.length);
  const v = findVariant(product, vals);
  const msg = dialog.querySelector('.lb-ce__msg');
  const btn = dialog.querySelector('[data-lb-ce-save]');
  let text = '';
  if (!v) text = 'That combination is not made. Pick another.';
  else if (!v.available) text = `${describe(controls, sel)} is sold out right now.`;
  msg.textContent = text;
  msg.hidden = !text;
  btn.disabled = !v || !v.available || v.id === line.variantId || state.busy;
  btn.textContent = state.busy ? 'Updating…' : 'Update cart';

  let choice = describe(controls, sel);
  if (v && v.id === line.variantId) choice += ' · in your cart now';
  else if (v && v.price !== line.unitPrice) choice += ` · ${money(v.price)} each`;
  dialog.querySelector('.lb-ce__choice').textContent = choice;

  const img = dialog.querySelector('.lb-ce__img');
  const src = (v && v.featured_image && v.featured_image.src) || line.image;
  if (src) {
    img.src = src + (src.includes('?') ? '&' : '?') + 'width=160';
    img.hidden = false;
  } else img.hidden = true;
}

/* "|Black=#181818|Brown / Peach=#3e3334,#e6ddce|Navy Plaid=photo|" -> {Black: '#181818', …} as CSS backgrounds */
function parseColors(s) {
  const out = {};
  for (const part of String(s || '').split('|')) {
    const i = part.lastIndexOf('=');
    if (i < 1) continue;
    const name = part.slice(0, i), val = part.slice(i + 1).trim();
    if (!val || val === 'photo') continue;
    const two = val.split(',');
    out[name] = two.length === 2 ? `linear-gradient(90deg, ${two[0]} 50%, ${two[1]} 50%)` : val;
  }
  return out;
}

function recFor(c, rec) {
  if (c.color) return null;
  if (/kid/i.test(c.label)) return rec.k || null;
  if (/adult/i.test(c.label)) return rec.a || null;
  if (/size/i.test(c.label)) return rec.a || rec.k || null;
  return null;
}

async function open(btn) {
  const row = btn.closest('tr[data-key]');
  const d = btn.dataset;
  const line = {
    key: d.key,
    variantId: Number(d.variant),
    handle: d.handle,
    qty: Number(d.qty) || 1,
    unitPrice: Number(d.price),
    title: d.title,
    url: d.url,
    image: row && row.querySelector('img') ? row.querySelector('img').currentSrc || row.querySelector('img').src : '',
    properties: d.props ? safeParse(d.props) : null,
    context: btn.closest('[data-drawer]') ? 'drawer' : 'page',
  };
  ensureDialog();
  btn.setAttribute('aria-busy', 'true');
  let product;
  try {
    product = await loadProduct(line.handle);
  } catch (e) {
    btn.removeAttribute('aria-busy');
    track('cart_edit_failed', { product: line.handle, stage: 'load', error: String(e.message || e) });
    return;
  }
  btn.removeAttribute('aria-busy');
  const variant = product.variants.find((v) => v.id === line.variantId);
  if (!variant) return;
  const controls = buildControls(product);
  // LAB-154: colors as dots (Tapstitch's values, snippets/lb-swatch-colors.liquid) and the line's own choice marked
  state = { product, controls, line, sel: selectionFromVariant(controls, variant), cartSel: selectionFromVariant(controls, variant),
            colors: parseColors(btn.dataset.lbColors), rec: readRec(line.handle), opener: btn, busy: false };
  dialog.querySelector('.lb-ce__product').textContent = line.title;
  const link = dialog.querySelector('.lb-ce__link');
  link.href = line.url || `${ROOT}products/${line.handle}`;
  render();
  dialog.showModal();
  const first = dialog.querySelector('.lb-ce__chips input:checked') || dialog.querySelector('.lb-ce__chips input');
  if (first) first.focus();
  track('cart_edit_opened', { product: line.handle, context: line.context, has_rec: !!state.rec });
}

function safeParse(s) {
  try { return JSON.parse(s); } catch (e) { return null; }
}

async function save() {
  if (!state || state.busy) return;
  const { product, controls, sel, line } = state;
  const v = findVariant(product, optionValues(controls, sel, product.options.length));
  if (!v || !v.available || v.id === line.variantId) return;
  const from = product.variants.find((x) => x.id === line.variantId);
  const fromLabel = describe(controls, selectionFromVariant(controls, from));
  const toLabel = describe(controls, sel);
  state.busy = true;
  render();
  const s = state;
  try {
    const { res, newKey, sections } = await swap({ fromKey: line.key, fromVariant: line.variantId, toVariant: v.id, qty: line.qty, properties: line.properties });
    const changed = controls.filter((c) => selectionFromVariant(controls, from)[c.id] !== sel[c.id]).map((c) => c.label);
    track('cart_edit_saved', { product: line.handle, context: line.context, from_variant: line.variantId, to_variant: v.id, changed, quantity: line.qty, used_rec: !!(s.rec && controls.some((c) => recFor(c, s.rec) === sel[c.id])) });
    close();
    announceCart(res, sections, newKey, line.qty, 'lb-cart-edit');
    const rows = await waitForRow(newKey, line.key);
    noteRows(rows, { text: `Changed to ${toLabel}.`, undo: { fromKey: newKey, fromVariant: v.id, toVariant: line.variantId, qty: line.qty, properties: line.properties, label: fromLabel, handle: line.handle } });
    const again = rows.map((r) => r.querySelector('[data-lb-edit]')).find((b) => b && b.offsetParent);
    if (again) again.focus();
  } catch (err) {
    s.busy = false;
    if (state === s) {
      render();
      const msg = dialog.querySelector('.lb-ce__msg');
      msg.textContent = err.userMessage || 'That did not go through. Your cart is unchanged.';
      msg.hidden = false;
    }
    track('cart_edit_failed', { product: line.handle, stage: 'save', error: String(err.message || err) });
  }
}

function noteRows(rows, { text, undo }) {
  for (const row of rows) {
    const cell = row.querySelector('.cart-items__variants-wrapper') || row.querySelector('.cart-items__details');
    if (!cell) continue;
    row.querySelectorAll('.lb-ce-note').forEach((n) => n.remove());
    const note = el('p', 'lb-ce-note');
    note.setAttribute('role', 'status');
    note.appendChild(el('span', null, text + ' '));
    if (undo) {
      const b = el('button', 'lb-ce-note__undo', 'Undo');
      b.type = 'button';
      b.addEventListener('click', () => runUndo(undo, b));
      note.appendChild(b);
    }
    cell.appendChild(note);
    row.classList.add('lb-ce-flash');
    setTimeout(() => row.classList.remove('lb-ce-flash'), 1600);
    if (undo) setTimeout(() => note.isConnected && note.querySelector('button') && note.querySelector('button').remove(), 15000);
  }
}

async function runUndo(u, btn) {
  btn.disabled = true;
  btn.textContent = 'Undoing…';
  try {
    const { res, newKey, sections } = await swap(u);
    track('cart_edit_undone', { product: u.handle, to_variant: u.toVariant });
    announceCart(res, sections, newKey, u.qty, 'lb-cart-edit-undo');
    const rows = await waitForRow(newKey, u.fromKey);
    noteRows(rows, { text: `Changed back to ${u.label}.` });
  } catch (err) {
    btn.disabled = false;
    btn.textContent = 'Undo';
    track('cart_edit_failed', { product: u.handle, stage: 'undo', error: String(err.message || err) });
  }
}

/* ---------------- wiring ---------------- */

document.addEventListener('click', (ev) => {
  const btn = ev.target instanceof Element ? ev.target.closest('[data-lb-edit]') : null;
  if (!btn) return;
  ev.preventDefault();
  open(btn);
});
// warm the product JSON on the first touch so the sheet opens without a wait
['pointerdown', 'focusin', 'mouseover'].forEach((type) =>
  document.addEventListener(
    type,
    (ev) => {
      const btn = ev.target instanceof Element ? ev.target.closest('[data-lb-edit]') : null;
      if (btn && btn.dataset.handle) loadProduct(btn.dataset.handle).catch(() => {});
    },
    { passive: true }
  )
);

function injectStyles() {
  const css = `
  .lb-ce{border:0;padding:0;margin:auto;width:min(440px,100vw);max-width:100vw;max-height:88vh;border-radius:12px;background:#fff;color:#1A1A1A;box-shadow:0 12px 40px rgb(0 0 0/.18)}
  .lb-ce::backdrop{background:rgb(26 26 26/.35)}
  @media (max-width:749px){.lb-ce{margin:auto 0 0;width:100vw;border-radius:14px 14px 0 0;max-height:85vh}}
  .lb-ce__inner{display:flex;flex-direction:column;gap:16px;padding:20px 16px calc(16px + env(safe-area-inset-bottom));overflow:auto;max-height:inherit;box-sizing:border-box}
  .lb-ce__head{display:flex;justify-content:space-between;align-items:flex-start;gap:12px}
  .lb-ce__title{font-size:1.05rem;margin:0;line-height:1.3}
  .lb-ce__product{margin:4px 0 0;font-size:.85rem;opacity:.7}
  .lb-ce__close{flex:0 0 auto;width:44px;height:44px;margin:-10px -8px 0 0;border:0;background:none;font-size:1.6rem;line-height:1;color:inherit;cursor:pointer}
  .lb-ce__summary{display:flex;align-items:center;gap:12px}
  .lb-ce__img{width:72px;height:72px;object-fit:cover;border-radius:6px;background:#F5F0E6}
  .lb-ce__choice{margin:0;font-size:.9rem}
  .lb-ce__body{display:flex;flex-direction:column;gap:16px}
  .lb-ce__group{border:0;margin:0;padding:0;min-width:0}
  .lb-ce__legend{font-size:.8rem;letter-spacing:.02em;margin:0 0 8px;padding:0;opacity:.75}
  .lb-ce__chips{display:flex;flex-wrap:wrap;gap:8px}
  .lb-ce__chip{position:relative;display:inline-flex;align-items:center;justify-content:center;min-width:44px;min-height:44px;padding:0 14px;border:1px solid rgb(26 26 26/.25);border-radius:8px;font-size:.9rem;cursor:pointer;box-sizing:border-box}
  .lb-ce__chip input{position:absolute;inset:0;opacity:0;margin:0;cursor:inherit}
  .lb-ce__chip:has(input:checked){border-color:#1A1A1A;background:#1A1A1A;color:#fff}
  .lb-ce__chip:has(input:focus-visible){outline:2px solid #1A1A1A;outline-offset:2px}
  .lb-ce__chip--soldout .lb-ce__name{text-decoration:line-through;opacity:.55}
  .lb-ce__chips:has(.lb-ce__chip--swatch){align-items:flex-start}
  .lb-ce__chip--swatch{flex-direction:column;gap:4px;padding:8px 6px 6px;min-width:68px;border-color:transparent}
  .lb-ce__chip--swatch:has(input:checked){background:none;color:#1A1A1A;border-color:#1A1A1A}
  .lb-ce__dot{width:30px;height:30px;border-radius:50%;box-shadow:inset 0 0 0 1px rgb(26 26 26/.18)}
  .lb-ce__chip--swatch .lb-ce__name{font-size:.75rem;line-height:1.2;text-align:center}
  .lb-ce__chip--incart{flex-direction:column}
  .lb-ce__incart{font-size:.65rem;letter-spacing:.04em;text-transform:uppercase;opacity:.7;line-height:1}
  .lb-ce__chip--none{opacity:.35;cursor:not-allowed}
  .lb-ce__chip--rec::after{content:"";position:absolute;top:5px;right:5px;width:6px;height:6px;border-radius:50%;background:currentColor}
  .lb-ce__rec{margin:8px 0 0;font-size:.8rem;opacity:.75}
  .lb-ce__msg{margin:0;font-size:.85rem;font-weight:600}
  .lb-ce__foot{display:flex;flex-direction:column;align-items:center;gap:12px}
  .lb-ce__save{width:100%;min-height:48px}
  .lb-ce__save:disabled{opacity:.45;cursor:not-allowed}
  .lb-ce__link{font-size:.8rem;color:inherit;opacity:.75}
  .lb-ce-note{margin:6px 0 0;font-size:.8rem}
  .lb-ce-note__undo{border:0;background:none;padding:4px 2px;min-height:24px;font:inherit;text-decoration:underline;color:inherit;cursor:pointer}
  tr.lb-ce-flash{animation:lbCeFlash 1.6s ease-out}
  @keyframes lbCeFlash{0%{background:#F5F0E6}100%{background:transparent}}
  @media (prefers-reduced-motion:reduce){tr.lb-ce-flash{animation:none}}`;
  const s = document.createElement('style');
  s.dataset.lbCartEdit = '';
  s.textContent = css;
  document.head.appendChild(s);
}
